import { ROUTES } from '../../consts/general';
import { TruckIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/solid';
import {
  ArrowDownOnSquareStackIcon,
  ArchiveBoxIcon,
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  ChartPieIcon,
  CreditCardIcon,
  CubeIcon,
  CubeTransparentIcon,
  CurrencyDollarIcon,
  DocumentChartBarIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  BellIcon,
  EnvelopeIcon,
  ShoppingCartIcon,
  TrophyIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import {
  CHILDREN_TAB_ACCOUNTS_PAYABLE,
  CHILDREN_TAB_APP_NOTES,
  CHILDREN_TAB_NOTIFICATION_SETTINGS,
  CHILDREN_TAB_BALANCE_SHEET,
  CHILDREN_TAB_BOM,
  CHILDREN_TAB_INVENTORY_RECLASSIFY,
  CHILDREN_TAB_BUDGET_X_FORECAST,
  CHILDREN_TAB_CASH_FLOW,
  CHILDREN_TAB_MEC_CLOSE_CHECKLIST,
  CHILDREN_TAB_CRON,
  CHILDREN_TAB_ORDERS,
  CHILDREN_TAB_COGS_CANONICAL_VIEW,
  CHILDREN_TAB_DEMAND_DESTRIBUTON_PROFILE,
  CHILDREN_TAB_DEMAND_PLANNING,
  CHILDREN_TAB_EFFECTIVE_CAPITAL_SUMMARY,
  CHILDREN_TAB_THIRTEEN_WEEK_CASH_FLOW,
  CHILDREN_TAB_EXISTED_SKUS_COMPARISON,
  CHILDREN_TAB_HOURLY_PERFORMANCE,
  CHILDREN_TAB_KLAVIYO_ANALYTICS,
  CHILDREN_TAB_TRYBE_CONTEST,
  CHILDREN_TAB_LOCATION_MASTER,
  CHILDREN_TAB_INVENTORY_LEDGER,
  CHILDREN_TAB_PICK_PACK_REPORT,
  CHILDREN_TAB_WAREHOUSE_ENGRAVING,
  CHILDREN_TAB_FBA_TRANSFERS,
  CHILDREN_TAB_AMAZON_FBA_INVENTORY,
  CHILDREN_TAB_INVENTORY_TRANSFERS,
  CHILDREN_TAB_RETURNS_RECEIVING,
  CHILDREN_TAB_OPEN_TO_BUY,
  CHILDREN_TAB_PRODUCTS_CATALOG,
  CHILDREN_TAB_MERCHANDISING_KPIS,
  CHILDREN_TAB_PROFIT_AND_LOSS,
  CHILDREN_TAB_PURCHASE_ORDERS,
  CHILDREN_TAB_RECEIVED_BOXES,
  CHILDREN_TAB_REPORTS,
  CHILDREN_TAB_SHIPMENTS,
  CHILDREN_TAB_SKU_MAPPING,
  CHILDREN_TAB_STOCK_LEVELS,
  CHILDREN_TAB_SUPPLIERS,
  CHILDREN_TAB_USERS,
  TAB_FINANCE,
  TAB_FULFILLMENT,
  TAB_MARKETING,
  TAB_MERCHANDISING,
  TAB_UTILITIES,
} from '../../consts/management/users/users';
import { EFFECTIVE_CAPITAL_TABS, isEffectiveCapitalTabVisible } from '../../features/finance/effectiveCapitalTabs';

export const demandPlanningPath = (view) => `/demand-planning?id=2&view=${view}`;
const effectiveCapitalPath = (tab) => `${ROUTES.EFFECTIVE_CAPITAL_SUMMARY}?tab=${tab}`;
const leaf = (id, label, path, icon, extra = {}) => ({
  type: 'leaf',
  id,
  permissionId: extra.permissionId || id,
  label,
  path,
  icon,
  ...extra,
});
const group = (id, label, children, icon, extra = {}) => ({ type: 'group', id, label, children, icon, ...extra });
const subgroup = (id, label, children, icon) => ({ type: 'subgroup', id, label, children, icon });

const effectiveCapitalChildren = EFFECTIVE_CAPITAL_TABS.map((tab) =>
  leaf(CHILDREN_TAB_EFFECTIVE_CAPITAL_SUMMARY, tab.navLabel, effectiveCapitalPath(tab.value), tab.icon, {
    permissionId: tab.permissionId,
    routePermissionId: CHILDREN_TAB_EFFECTIVE_CAPITAL_SUMMARY,
    searchAliases: tab.searchAliases,
    isVisible: (user, allowedPermissionIds) =>
      isEffectiveCapitalTabVisible(tab.value, user, allowedPermissionIds),
  })
);

const demandPlanningViews = [
  ['forecast-demand', 'Forecast Demand', ArrowTopRightOnSquareIcon, 'nav-demand-planning-forecast-demand', ['fd', 'demand forecast']],
  ['sku-allocation', 'SKU Allocation', ChartPieIcon, 'nav-demand-planning-sku-allocation', ['allocation']],
  ['forecast-vs-actuals', 'Forecast Vs. Actuals', ChartBarIcon, 'nav-demand-planning-forecast-vs-actuals', ['fva', 'actuals', 'variance']],
  ['ai-rebalance', 'Demand Rebalance', ArrowDownOnSquareStackIcon, 'nav-demand-planning-ai-rebalance', ['ai rebalance', 'rebalance']],
  ['forecast-by-sku', 'Forecast by SKU', DocumentChartBarIcon, 'nav-demand-planning-forecast-by-sku', ['fbs']],
  ['monthly-trend', 'Monthly Trend', ChartBarIcon, 'nav-demand-planning-monthly-trend', ['trend']],
  ['historical', 'Historical', DocumentTextIcon, 'nav-demand-planning-historical', ['history']],
].map(([id, label, icon, permissionId, searchAliases]) => leaf(CHILDREN_TAB_DEMAND_PLANNING, label, demandPlanningPath(id), icon, {
  key: id,
  permissionId,
  routePermissionId: CHILDREN_TAB_DEMAND_PLANNING,
  searchAliases,
}));

const replenishmentViews = [
  ['open-to-buy', 'Open to Buy', BuildingLibraryIcon, 'nav-replenishment-open-to-buy', ['otb']],
  ['approved-buys', 'Approved Buys', BuildingStorefrontIcon, 'nav-replenishment-approved-buys', ['ab']],
  ['upcoming-buys', 'Upcoming Buys', ShoppingCartIcon, 'nav-replenishment-upcoming-buys', ['ub']],
  ['purchase-rounding', 'Purchase Rounding', CubeIcon, 'nav-replenishment-purchase-rounding', ['moq', 'rounding']],
].map(([id, label, icon, permissionId, searchAliases]) => leaf(CHILDREN_TAB_OPEN_TO_BUY, label, demandPlanningPath(id), icon, {
  key: id,
  permissionId,
  routePermissionId: CHILDREN_TAB_DEMAND_PLANNING,
  searchAliases,
}));

const capitalRiskViews = [
  ['inventory-capital-plan', 'Inventory Capital Plan', BuildingOfficeIcon, 'nav-capital-risk-inventory-capital-plan', ['icp', 'capital plan']],
  ['cash-liabilities', 'Cash Liabilities', BanknotesIcon, 'nav-capital-risk-cash-liabilities', ['liabilities']],
  ['overstock-inventory', 'Overstock Inventory', BuildingOffice2Icon, 'nav-capital-risk-overstock-inventory', ['overstock']],
].map(([id, label, icon, permissionId, searchAliases]) => leaf(CHILDREN_TAB_OPEN_TO_BUY, label, demandPlanningPath(id), icon, {
  key: id,
  permissionId,
  routePermissionId: CHILDREN_TAB_DEMAND_PLANNING,
  searchAliases,
}));

const planningControlViews = [
  ['eligibility-validation', 'Eligibility & Validation', DocumentMagnifyingGlassIcon, CHILDREN_TAB_DEMAND_PLANNING, 'nav-demand-planning-eligibility-validation', ['eligibility']],
  ['demand-distribution-profile', 'Demand Distribution Profiles', ChartPieIcon, CHILDREN_TAB_DEMAND_DESTRIBUTON_PROFILE, 'nav-demand-distribution-profile', ['ddp', 'profiles']],
].map(([id, label, icon, routePermissionId, permissionId, searchAliases]) => leaf(routePermissionId, label, demandPlanningPath(id), icon, {
  key: id,
  permissionId,
  routePermissionId: CHILDREN_TAB_DEMAND_PLANNING,
  searchAliases,
}));

const transformationViews = [
  ['bundle-demand-explosion', 'Bundle Demand Explosion', CubeIcon, 'nav-demand-transformations-bundle-demand-explosion', ['bde', 'bundles']],
  ['replacement-demand-shift', 'Replacement Demand Shift', ArrowTopRightOnSquareIcon, 'nav-demand-transformations-replacement-demand-shift'],
  ['bundle-replacement-shift', 'Bundle Replacement Shift', ArrowTopRightOnSquareIcon, 'nav-demand-transformations-bundle-replacement-shift'],
  ['component-demand-ledger', 'Component Demand Ledger', DocumentChartBarIcon, 'nav-demand-transformations-component-demand-ledger', ['cdl']],
].map(([id, label, icon, permissionId, searchAliases]) => leaf(CHILDREN_TAB_OPEN_TO_BUY, label, demandPlanningPath(id), icon, {
  key: id,
  permissionId,
  routePermissionId: CHILDREN_TAB_DEMAND_PLANNING,
  searchAliases,
}));

export const menuConfig = [
  {
    type: 'pinned',
    id: CHILDREN_TAB_HOURLY_PERFORMANCE,
    permissionId: 'hourly-performance',
    label: 'Command Center',
    path: ROUTES.ANALYTICS_TRIPLEWHALE,
    icon: ChartBarIcon,
    searchAliases: ['dashboard', 'triplewhale'],
  },
  {
    type: 'section',
    id: TAB_FINANCE,
    label: 'FINANCE',
    icon: CurrencyDollarIcon,
    children: [
      group('effective-capital', 'Effective Capital', effectiveCapitalChildren, CurrencyDollarIcon),
      group(
        'thirteen-week-cash-flow',
        '13-Week Cash Flow',
        [
          ['summary', 'Summary'],
          ['daily', 'Daily Cash Forecast'],
          ['cashReview', 'Cash Activity Review'],
          ['revenue', 'Revenue Plan'],
          ['disbursements', 'Disbursement Calendar'],
          ['config', 'Configuration'],
        ].map(([tab, label]) => leaf(
          CHILDREN_TAB_THIRTEEN_WEEK_CASH_FLOW,
          label,
          `${ROUTES.THIRTEEN_WEEK_CASH_FLOW}?tab=${tab}`,
          BanknotesIcon,
          {
            permissionId: CHILDREN_TAB_THIRTEEN_WEEK_CASH_FLOW,
            routePermissionId: CHILDREN_TAB_THIRTEEN_WEEK_CASH_FLOW,
            searchAliases: ['13 week', '13-week', 'cash flow forecast', 'weekly cash'],
          }
        )),
        BanknotesIcon
      ),
      group(
        'financial-statements',
        'Financial Statements',
        [
          leaf(CHILDREN_TAB_PROFIT_AND_LOSS, 'Profit & Loss', ROUTES.PROFIT_AND_LOSS, DocumentMagnifyingGlassIcon, { permissionId: 'profit-and-loss', searchAliases: ['pnl', 'p&l', 'income statement'] }),
          leaf(CHILDREN_TAB_REPORTS, 'Amazon P&L Summary', ROUTES.REPORTS_AMAZON_PL_SUMMARY, DocumentChartBarIcon, { permissionId: 'amazon-pl-summary', routePermissionId: CHILDREN_TAB_REPORTS, searchAliases: ['amazon pnl', 'amazon profit and loss'] }),
          leaf(CHILDREN_TAB_BALANCE_SHEET, 'Balance Sheet', ROUTES.BALANCE_SHEET, BuildingOffice2Icon, { permissionId: 'balance-sheet', searchAliases: ['bs'] }),
          leaf(CHILDREN_TAB_CASH_FLOW, 'Cash Flow', ROUTES.CASH_FLOW, BanknotesIcon, { permissionId: 'cash-flow', searchAliases: ['cf'] }),
          leaf(CHILDREN_TAB_COGS_CANONICAL_VIEW, 'COGS', ROUTES.COGS_CANONICAL_VIEW, ChartBarIcon, { permissionId: 'cogs-canonical-view', searchAliases: ['cost of goods sold'] }),
          leaf(CHILDREN_TAB_BUDGET_X_FORECAST, 'Budget', ROUTES.BUDGET_X_FORECAST, ArrowDownOnSquareStackIcon, { permissionId: 'budget-x-forecast', searchAliases: ['bvf', 'budget vs forecast'] }),
        ],
        BuildingOffice2Icon
      ),
      leaf(CHILDREN_TAB_MEC_CLOSE_CHECKLIST, 'Close Checklist', ROUTES.MEC_CLOSE_CHECKLIST, DocumentChartBarIcon, { permissionId: CHILDREN_TAB_MEC_CLOSE_CHECKLIST, searchAliases: ['month end close', 'mec'] }),
      leaf(CHILDREN_TAB_ACCOUNTS_PAYABLE, 'Accounts Payable', ROUTES.FINANCE, CreditCardIcon, { permissionId: 'accounts-payable', searchAliases: ['ap', 'bills'] }),
      leaf(CHILDREN_TAB_REPORTS, 'Reports', ROUTES.REPORTS, DocumentChartBarIcon, { permissionId: 'reports' }),
      leaf(CHILDREN_TAB_REPORTS, 'Data Reconciliation', ROUTES.REPORTS_DATA_RECONCILIATION, DocumentMagnifyingGlassIcon, { permissionId: 'data-reconciliation', routePermissionId: CHILDREN_TAB_REPORTS, searchAliases: ['recon'] }),
    ],
  },
  {
    type: 'section',
    id: TAB_MERCHANDISING,
    label: 'MERCHANDISING',
    icon: BuildingOffice2Icon,
    children: [
      leaf(CHILDREN_TAB_MERCHANDISING_KPIS, 'Merchandising KPIs', ROUTES.MERCHANDISING_KPIS, ChartBarIcon, { permissionId: CHILDREN_TAB_MERCHANDISING_KPIS, searchAliases: ['merchandising', 'sell through', 'sku performance', 'buying priorities'] }),
      group(
        'catalog-management',
        'Catalog Management',
        [
          leaf(CHILDREN_TAB_PRODUCTS_CATALOG, 'Product Catalog', ROUTES.PRODUCTS_CATALOG, BuildingOffice2Icon, { permissionId: 'products-catalog', searchAliases: ['catalog', 'products'] }),
          leaf(CHILDREN_TAB_SKU_MAPPING, 'SKU Mapping', ROUTES.SKU_MAPPING, ChartPieIcon, { permissionId: 'sku-mapping', searchAliases: ['mapping'] }),
          leaf(CHILDREN_TAB_BOM, 'Bills of Materials', ROUTES.BOM, ChartBarIcon, { permissionId: 'bom', searchAliases: ['bom'] }),
          leaf(CHILDREN_TAB_INVENTORY_RECLASSIFY, 'Reclassify Inventory', ROUTES.INVENTORY_RECLASSIFY, ArrowsRightLeftIcon, { permissionId: CHILDREN_TAB_INVENTORY_RECLASSIFY, searchAliases: ['reclassify', 'outlet', 'change sku'] }),
          leaf(CHILDREN_TAB_EXISTED_SKUS_COMPARISON, 'Existing SKU Comparison', ROUTES.EXISTED_SKUS, DocumentTextIcon, { permissionId: 'existed-skus-comparison', permissionBypass: true, searchAliases: ['sku comparison'] }),
        ],
        BuildingOffice2Icon
      ),
      group(
        'inventory-planning',
        'Inventory Planning',
        [
          leaf(CHILDREN_TAB_STOCK_LEVELS, 'Inventory Overview', ROUTES.SHOPIFY_AMAZON_INVENTORY, ChartBarIcon, { permissionId: 'stock-levels', searchAliases: ['stock levels', 'inventory'] }),
          subgroup('demand-planning', 'Demand Planning', demandPlanningViews, ArrowTopRightOnSquareIcon),
          subgroup('replenishment-planning', 'Replenishment Planning', replenishmentViews, BuildingLibraryIcon),
          subgroup('inventory-capital-risk', 'Inventory & Capital Risk', capitalRiskViews, BuildingOfficeIcon),
          subgroup('planning-controls', 'Planning Controls', planningControlViews, DocumentMagnifyingGlassIcon),
          subgroup('demand-transformations', 'Demand Transformations', transformationViews, CubeIcon),
          leaf(CHILDREN_TAB_OPEN_TO_BUY, 'Container Optimization', demandPlanningPath('container-optimization'), BuildingStorefrontIcon, {
            permissionId: 'nav-replenishment-container-optimization',
            routePermissionId: CHILDREN_TAB_DEMAND_PLANNING,
            searchAliases: ['containers'],
          }),
        ],
        ChartBarIcon
      ),
      group(
        'purchasing',
        'Purchasing',
        [
          leaf(CHILDREN_TAB_PURCHASE_ORDERS, 'Purchase Orders', ROUTES.PURCHASE_ORDERS, BuildingStorefrontIcon, { permissionId: 'purchase-orders', searchAliases: ['po', 'pos'] }),
          leaf(CHILDREN_TAB_SUPPLIERS, 'Suppliers', ROUTES.VENDORS, BuildingStorefrontIcon, { permissionId: 'suppliers', searchAliases: ['vendors'] }),
        ],
        BuildingStorefrontIcon
      ),
    ],
  },
  {
    type: 'section',
    id: TAB_MARKETING,
    label: 'MARKETING',
    icon: EnvelopeIcon,
    children: [
      leaf(CHILDREN_TAB_KLAVIYO_ANALYTICS, 'Retention Analytics', ROUTES.KLAVIYO_ANALYTICS, EnvelopeIcon, { permissionId: 'klaviyo-analytics', searchAliases: ['retention', 'klaviyo', 'postscript', 'email', 'sms'] }),
      leaf(CHILDREN_TAB_TRYBE_CONTEST, 'Trybe Creator Contest', ROUTES.TRYBE_CONTEST, TrophyIcon, { permissionId: CHILDREN_TAB_TRYBE_CONTEST, searchAliases: ['trybe', 'creator', 'contest', 'leaderboard', 'ugc'] }),
    ],
  },
  {
    type: 'section',
    id: TAB_FULFILLMENT,
    label: 'FULFILLMENT',
    icon: TruckIcon,
    children: [
      leaf(CHILDREN_TAB_SHIPMENTS, 'Inbound Shipments', ROUTES.SHIPPING, TruckIcon, { permissionId: 'shipments', searchAliases: ['shipments', 'inbound'] }),
      leaf(CHILDREN_TAB_RECEIVED_BOXES, 'Receiving', ROUTES.RECEIVED_SESSIONS, CubeIcon, { permissionId: 'received-boxes', searchAliases: ['received', 'receipts'] }),
      group(
        'fulfillment-warehouse',
        'Warehouse',
        [
          leaf(CHILDREN_TAB_LOCATION_MASTER, 'Location Master', ROUTES.WAREHOUSE, CubeTransparentIcon, {
            permissionId: CHILDREN_TAB_LOCATION_MASTER,
            searchAliases: ['warehouse', 'digital twin', 'locations', 'racks'],
          }),
          leaf(CHILDREN_TAB_INVENTORY_LEDGER, 'Inventory Ledger', ROUTES.REPORTS_INVENTORY_LEDGER, CubeTransparentIcon, {
            permissionId: CHILDREN_TAB_INVENTORY_LEDGER,
            searchAliases: ['inventory', 'changes', 'ledger', 'shiphero'],
          }),
          leaf(CHILDREN_TAB_PICK_PACK_REPORT, 'Pick & Pack Report', ROUTES.REPORTS_PICK_PACK, CubeTransparentIcon, {
            permissionId: CHILDREN_TAB_PICK_PACK_REPORT,
            searchAliases: ['pick', 'pack', 'picker', 'packer', 'productivity', 'shiphero'],
          }),
          leaf(CHILDREN_TAB_WAREHOUSE_ENGRAVING, 'Engraving', '/warehouse/engraving', CubeTransparentIcon, {
            permissionId: CHILDREN_TAB_WAREHOUSE_ENGRAVING,
            searchAliases: ['laser', 'engrave', 'lp4', 'tote'],
          }),
          leaf(CHILDREN_TAB_FBA_TRANSFERS, 'Transfers', ROUTES.FBA_TRANSFERS, ArrowsRightLeftIcon, {
            permissionId: CHILDREN_TAB_FBA_TRANSFERS,
            searchAliases: ['fba', 'amazon', 'transfer', 'inbound to amazon', 'seller central'],
          }),
          leaf(CHILDREN_TAB_AMAZON_FBA_INVENTORY, 'Amazon FBA Inventory', ROUTES.AMAZON_FBA_INVENTORY, ArchiveBoxIcon, {
            permissionId: CHILDREN_TAB_AMAZON_FBA_INVENTORY,
            searchAliases: ['fba', 'amazon', 'fba inventory', 'fnsku', 'asin', 'stranded', 'sellable', 'fulfillable'],
          }),
          leaf(CHILDREN_TAB_INVENTORY_TRANSFERS, 'Inventory Transfer', ROUTES.INVENTORY_TRANSFERS, ArrowsRightLeftIcon, {
            permissionId: CHILDREN_TAB_INVENTORY_TRANSFERS,
            searchAliases: ['move', 'move stock', 'bin to bin', 'location transfer', 'location transfers', 'inventory transfer', 'relocate'],
          }),
          leaf(CHILDREN_TAB_RETURNS_RECEIVING, 'Returns Receiving', ROUTES.RETURNS_RECEIVING, ArchiveBoxIcon, {
            permissionId: CHILDREN_TAB_RETURNS_RECEIVING,
            searchAliases: ['returns', 'return labels', 'rma receiving', 'scan return'],
          }),
        ],
        CubeTransparentIcon
      ),
      leaf(CHILDREN_TAB_ORDERS, 'Customer Orders', ROUTES.ORDERS, ShoppingCartIcon, { permissionId: 'orders', searchAliases: ['orders'] }),
    ],
  },
  {
    type: 'section',
    id: TAB_UTILITIES,
    label: 'SYSTEM',
    icon: WrenchScrewdriverIcon,
    children: [
      leaf(CHILDREN_TAB_USERS, 'Users & Permissions', ROUTES.USERS_SETTINGS, UsersIcon, { permissionId: 'users', searchAliases: ['users', 'permissions', 'roles', 'rbac'] }),
      leaf(CHILDREN_TAB_CRON, 'System Jobs', ROUTES.CRON, WrenchScrewdriverIcon, { permissionId: 'cron', searchAliases: ['cron', 'jobs'] }),
      leaf(CHILDREN_TAB_APP_NOTES, 'App Notes', ROUTES.NOTES, DocumentTextIcon, { permissionId: 'app-notes', searchAliases: ['notes'] }),
      leaf(CHILDREN_TAB_NOTIFICATION_SETTINGS, 'Notification Settings', ROUTES.NOTIFICATIONS_SETTINGS, BellIcon, { permissionId: 'notification-settings', searchAliases: ['notifications'] }),
    ],
  },
];

export const getPermissionCatalog = (
  nodes = menuConfig,
  sectionId = null,
  sectionLabel = '',
  modulePath = []
) =>
  nodes.flatMap((node) => {
    const nextSectionId = node.type === 'section' ? node.id : sectionId;
    const nextSectionLabel = node.type === 'section' ? node.label : sectionLabel;
    const nextModulePath =
      node.type === 'section' || node.type === 'pinned'
        ? modulePath
        : node.type === 'leaf'
          ? modulePath
          : [...modulePath, node.label].filter(Boolean);
    const isLeaf = !node.permissionBypass && Boolean(node.path) && (!node.children || node.type === 'leaf' || node.type === 'pinned');

    if (isLeaf) {
      return [{
        permissionId: node.permissionId || node.id,
        routePermissionId: node.routePermissionId || node.id,
        parentId: nextSectionId || (node.type === 'pinned' ? TAB_FINANCE : node.id),
        sectionLabel: nextSectionLabel || (node.type === 'pinned' ? 'FINANCE' : node.label),
        modulePath: nextModulePath,
        label: node.label,
        path: node.path,
      }];
    }

    return node.children
      ? getPermissionCatalog(node.children, nextSectionId, nextSectionLabel, nextModulePath)
      : [];
  });

export const getPermissionRouteAliases = (nodes = menuConfig) =>
  getPermissionCatalog(nodes).reduce((aliases, entry) => {
    if (entry.routePermissionId && entry.routePermissionId !== entry.permissionId) {
      aliases[entry.permissionId] = entry.routePermissionId;
    }
    return aliases;
  }, {});

const demandPlanningViewPermissionIds = new Map(
  getPermissionCatalog()
    .filter(({ path }) => path.includes('/demand-planning?'))
    .map(({ path, permissionId }) => [new URLSearchParams(path.split('?')[1]).get('view'), permissionId])
);

export const getDemandPlanningViewPermissionId = (viewId, nodes = menuConfig) => {
  if (nodes === menuConfig) return demandPlanningViewPermissionIds.get(viewId);
  return getPermissionCatalog(nodes).find((entry) => entry.path.includes(`view=${viewId}`))?.permissionId;
};
